import { Button, Input } from '@breetometer/components';
import { Beneficiaries, FiatInput, NotificationBanner } from 'BreetComponents';
import {
	alwaysRefetchApiQueryOptions,
	checkNearestValidAmount,
	formatNumberWithCommas,
	getCleanedValue,
	getNearestValidAmount,
	getWithdrawRules,
	handleDecimalPoints,
	type IBeneficiary,
} from 'BreetHelpers';
import { useCurrencyOutput } from 'BreetHooks';
import { useGetFeatureNoteQuery } from 'BreetRedux';
import { type ChangeEvent, useCallback, useState } from 'react';

import type { IWithdrawFormComponent } from '../DialogWithdrawRequest.helpers';

const FormComponent = ({
	onNext,
	onAddNewBeneficiary,
	currencyProps,
	userWalletProps,
	onManageBeneficiaries,
	withdrawRequestHookForm,
	meta,
}: IWithdrawFormComponent) => {
	const currencyOutput = useCurrencyOutput();

	const { withdrawalMaxAmount, withdrawalMinAmount } = getWithdrawRules(meta?.settings, currencyProps?.code);

	const [inputMessage, setInputMessage] = useState('');

	const {
		setError,
		clearErrors,
		watch,
		setValue,
		formState: { errors },
		register,
		handleSubmit,
	} = withdrawRequestHookForm;

	const isNGN = currencyProps?.code === 'NGN';

	const [inputValue, setInputValue] = useState<string>(() => currencyOutput(watch('amount')) ?? '0');

	const validateAmount = useCallback(
		(cleanedValue: string) => {
			const numericAmount = Number(cleanedValue.replace(/,/g, ''));

			if (numericAmount > userWalletProps.walletBalance) {
				setError('amount', {
					message: 'Insufficient balance',
				});
				return;
			}

			if (numericAmount < withdrawalMinAmount) {
				setError('amount', {
					message: `The minimum withdrawal amount is ${currencyProps?.symbol} ${formatNumberWithCommas(withdrawalMinAmount)}`,
				});
				return;
			}

			if (!!withdrawalMaxAmount && numericAmount > withdrawalMaxAmount) {
				setError('amount', {
					message: `The maximum withdrawal amount is ${currencyProps?.symbol} ${formatNumberWithCommas(withdrawalMaxAmount)}`,
				});
				return;
			}

			const nearestValidAmountError = checkNearestValidAmount(numericAmount, currencyProps?.code);

			if (nearestValidAmountError) {
				setError('amount', { message: nearestValidAmountError });
				return;
			}
			clearErrors('amount');
		},
		[
			userWalletProps.walletBalance,
			withdrawalMinAmount,
			withdrawalMaxAmount,
			currencyProps?.code,
			currencyProps?.symbol,
			clearErrors,
			setError,
		]
	);

	const handleAmountChange = useCallback(
		(event: ChangeEvent<HTMLInputElement>) => {
			const { value } = event.target;

			if (!!errors.amount?.message || !value) clearErrors('amount');

			let cleanedValue = getCleanedValue(value);

			cleanedValue = handleDecimalPoints(cleanedValue);

			setInputValue(currencyOutput(cleanedValue) ?? '');

			setValue('amount', cleanedValue, { shouldDirty: true });

			validateAmount(cleanedValue);

			if (inputMessage) {
				setInputMessage('');
			}
		},
		[errors.amount?.message, clearErrors, currencyOutput, setValue, validateAmount, inputMessage]
	);

	const handleMaxClick = useCallback(() => {
		const walletBalance = userWalletProps.walletBalance;

		let amount = Math.min(walletBalance, withdrawalMaxAmount);

		if (inputMessage) {
			setInputMessage('');
		}

		if (walletBalance > withdrawalMaxAmount) {
			setInputMessage(
				`Maximum withdrawal per transaction is ${currencyProps?.symbol} ${formatNumberWithCommas(withdrawalMaxAmount)}`
			);
		}

		amount = getNearestValidAmount(amount, isNGN);

		let cleanedValue = getCleanedValue(amount.toString());

		cleanedValue = handleDecimalPoints(cleanedValue);

		setInputValue(currencyOutput(cleanedValue) ?? '');

		setValue('amount', cleanedValue, { shouldDirty: true });

		validateAmount(cleanedValue);
	}, [
		currencyOutput,
		currencyProps?.symbol,
		inputMessage,
		isNGN,
		setValue,
		userWalletProps.walletBalance,
		validateAmount,
		withdrawalMaxAmount,
	]);

	const selectBeneficiary = useCallback(
		(beneficiary: IBeneficiary | undefined) => {
			if (!beneficiary) return;

			setValue('selectedBeneficiary', {
				accountName: beneficiary.accountName,
				accountNumber: beneficiary.accountNumber,
				bankName: beneficiary.bankName,
				id: beneficiary._id,
			});

			setValue('narration', beneficiary.narration ?? '');
		},
		[setValue]
	);

	const isFormValid = !errors.amount?.message && !!watch('selectedBeneficiary.id') && !!watch('amount');

	const { data: featureNotesData } = useGetFeatureNoteQuery(undefined, alwaysRefetchApiQueryOptions);

	const featureNotes = featureNotesData?.data;

	const noteData = featureNotes?.find(({ feature, type }) => feature === 'wallet' && type === 'notes');

	return (
		<form
			onSubmit={handleSubmit(onNext)}
			className='payment-request-form'
			data-testid='withdraw-form'
			noValidate
		>
			<FiatInput
				label='Enter Amount'
				currencyCode='NGN'
				walletDetails={userWalletProps}
				handleMaxClick={handleMaxClick}
				onChange={handleAmountChange}
				errorMessage={errors.amount?.message}
				message={inputMessage}
				value={inputValue}
			/>

			<div className='payment-request-form_notificationbanner'>
				<NotificationBanner
					message={noteData?.message}
					status='error'
					type='standard'
				/>
			</div>

			<Beneficiaries
				onAddNewBeneficiary={onAddNewBeneficiary}
				selectBeneficiary={selectBeneficiary}
				selectedBeneficiaryId={watch('selectedBeneficiary').id}
				title='Select Destination'
				onManageBeneficiaries={onManageBeneficiaries}
			/>

			<div className='narration-input'>
				<Input
					id='narration'
					label='Narration'
					widthType='full'
					{...register('narration')}
					errorMessage={errors.narration?.message}
					required
				/>
			</div>

			<div className='payment-request-form_cta'>
				<Button
					label='Next'
					type='submit'
					className='right-sheet_cta_fullwidth'
					disabled={!isFormValid}
				/>
			</div>
		</form>
	);
};

export default FormComponent;
